import type React from "react";
import type { FieldProps } from "../../../types/FormTypes";
import Field from "../Field/Field";
import { useField } from "../Form/Form";
import RadioGroup, { type RadioGroupProps } from "./RadioGroup";

export interface RadioGroupFieldProps<TValue extends string = string>
  extends Omit<
      RadioGroupProps<TValue>,
      "ariaLabel" | "error" | "fullWidth" | "onBlur" | "onChange" | "onValueChange" | "value"
    >,
    Omit<FieldProps<TValue>, "error"> {
  ariaLabel?: string;
  error?: boolean | string;
}

const RadioGroupField = <TValue extends string = string>({
  ariaLabel,
  children,
  className,
  disabled,
  error,
  fullWidth = false,
  helperText,
  label,
  name,
  onBlur,
  onChange,
  onValueChange,
  required,
  value: controlledValue,
  ...groupProps
}: RadioGroupFieldProps<TValue>) => {
  const field = useField<TValue>({ name, value: controlledValue, onChange, onValueChange, required });
  const errorMessage = typeof error === "string" ? error : undefined;
  const hasError = Boolean(field.error || errorMessage || error === true);

  return (
    <Field
      className={className}
      name={name}
      fullWidth={fullWidth}
    >
      {label ? <Field.Label required={field.required}>{label}</Field.Label> : null}
      <Field.Control>
        <RadioGroup
          {...groupProps}
          ariaLabel={ariaLabel ?? (typeof label === "string" ? label : (name ?? "Options"))}
          name={name}
          value={field.value}
          onValueChange={field.onValueChange}
          onBlur={field.onBlur}
          disabled={disabled}
          error={hasError}
          required={field.required}
        >
          {children}
        </RadioGroup>
      </Field.Control>
      <Field.Message
        error={hasError}
        message={errorMessage ?? helperText}
      />
    </Field>
  );
};

export default RadioGroupField;
